import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';

// MUI
import Container from '@mui/material/Container';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import logoIconDark from 'assets/images/logo.svg';

// App
import { errorMessage } from '../../utils/constants';
import Loader from '../common/Loader';
import AddRemovalSuccess from './AddRemovalSuccess';
import RemovalService from '../../services/removal.service';

const AddRemovalForm = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [email, setEmail] = useState('');

    // 'soft' keeps the entered email (Try Again on error); anything else clears it.
    const resetForm = mode => {
        setError(null);
        setSuccess(false);
        if (mode !== 'soft') {
            setEmail('');
        }
    };

    const submit = async values => {
        setLoading(true);
        setEmail(values.email);
        try {
            const data = await RemovalService.requestAccountRemoval(values.email);
            if (data?.res) {
                setSuccess(true);
            } else {
                setError(data?.error || true);
            }
        } catch (err) {
            setError(err || true);
        } finally {
            setLoading(false);
        }
    };

    const initialValues = {
        email: email || ''
    };

    const validationSchema = Yup.object().shape({
        email: Yup.string()
            .trim()
            .lowercase()
            .email(errorMessage.email.valid)
            .required(errorMessage.email.required)
    });

    if (loading) {
        return (
            <Box
                sx={{
                    flex: 1,
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <Loader />
            </Box>
        );
    }

    if (success || error) {
        return (
            <Box
                sx={{
                    flex: 1,
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <AddRemovalSuccess error={error} resetForm={resetForm} />
            </Box>
        );
    }

    return (
        <Container maxWidth="sm">
            <Formik
                initialValues={initialValues}
                onSubmit={submit}
                validationSchema={validationSchema}
                validateOnChange
                enableReinitialize
            >
                {({
                    errors,
                    touched,
                    handleBlur,
                    handleChange,
                    handleSubmit,
                    values
                }) => (
                    <Form onSubmit={handleSubmit} noValidate>
                        <Card elevation={6} sx={{ borderRadius: 3 }}>
                            <CardContent>
                                <Stack
                                    spacing={3}
                                    alignItems="center"
                                    textAlign="center"
                                    sx={{ mt: 2 }}
                                >
                                    <Box
                                        component="img"
                                        src={logoIconDark}
                                        alt="Sage logo"
                                        sx={{ width: 80, height: 'auto' }}
                                    />
                                    <Typography variant="h4" fontWeight={600}>
                                        Exit from SAGE
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary">
                                        To request account removal, please enter the email
                                        address associated with your Sage account. Once
                                        submitted, your community management team will
                                        verify your request and promptly remove your
                                        account. Thank you for using Sage!
                                    </Typography>
                                </Stack>

                                <Stack spacing={2} sx={{ mt: 4 }}>
                                    <TextField
                                        fullWidth
                                        required
                                        id="email"
                                        name="email"
                                        label="Email Address"
                                        placeholder="you@example.com"
                                        value={values.email}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={Boolean(touched.email && errors.email)}
                                        helperText={
                                            touched.email && errors.email
                                                ? errors.email
                                                : ' '
                                        }
                                        autoComplete="email"
                                    />
                                </Stack>
                            </CardContent>

                            <Divider />

                            <CardActions sx={{ justifyContent: 'flex-end', p: 3 }}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disableElevation
                                >
                                    Submit Request
                                </Button>
                            </CardActions>
                        </Card>
                    </Form>
                )}
            </Formik>
        </Container>
    );
};

export default AddRemovalForm;
